/**
 * Shared trade normalization for backtest views.
 * Keeps snake_case/camelCase compatibility and stable numeric fields.
 */

//* 144-6 R5-T5 - canonical component names come from the managed-schedule calculator
//* (`commission_breakdown.components`); the legacy aliases keep STORED historical results readable
//* (display-only fallback, never a rate source).
const LEGACY_COMPONENT_ALIASES = { stt: 'stt_ctt', exchange_tx: 'exchange_txn', stamp_duty: 'stamp' };

/** readChargeComponents - {componentName: amount} from a stored trade `commission_breakdown`. */
export const readChargeComponents = (breakdown) => {
    if (!breakdown || typeof breakdown !== 'object') return {};
    const out = {};
    if (breakdown.components && typeof breakdown.components === 'object') {
        for (const [name, amount] of Object.entries(breakdown.components)) {
            const n = Number(amount);
            if (Number.isFinite(n)) out[name] = n;
        }
        return out;
    }
    for (const key of ['brokerage', 'sebi', 'gst', ...Object.keys(LEGACY_COMPONENT_ALIASES)]) {
        const n = Number(breakdown[key]);
        if (breakdown[key] != null && Number.isFinite(n)) out[LEGACY_COMPONENT_ALIASES[key] || key] = n;
    }
    return out;
};

export const normalizeBacktestTrades = (trades = []) => {
    if (!Array.isArray(trades)) return [];

    return trades.map((t) => {
        const sideRaw = t.side ?? t.position_side ?? '';
        const side = String(sideRaw).toUpperCase();
        const pnl = Number(t.pnl ?? t.net_pnl ?? 0);
        //--> total charges: the trade's own total, else its breakdown total, else the legacy `fees`; `brokerage` is a
        //    COMPONENT (never the total), so it is no longer a fallback.
        const commission = Number(t.commission ?? t.commission_breakdown?.total ?? t.fees ?? 0);

        return {
            ...t,
            side: side === 'LONG' || side === 'BUY' ? 'LONG' : 'SHORT',
            entryTime: t.entryTime ?? t.entry_time ?? null,
            exitTime: t.exitTime ?? t.exit_time ?? null,
            entryPrice: Number(t.entryPrice ?? t.entry_price ?? 0),
            exitPrice: t.exitPrice ?? t.exit_price ?? null,
            pnl,
            pnlPct: Number(t.pnlPct ?? t.pnl_pct ?? t.net_pnl_pct ?? 0),
            commission,
            //--> R2-F2: gross / statutory are API fields (null on results stored before 144-6), never derived in the browser.
            grossPnl: Number.isFinite(Number(t.gross_pnl ?? t.grossPnl)) && (t.gross_pnl ?? t.grossPnl) !== null ? Number(t.gross_pnl ?? t.grossPnl) : null,
            statutoryCharges: Number.isFinite(Number(t.statutory_charges)) && t.statutory_charges !== null && t.statutory_charges !== undefined ? Number(t.statutory_charges) : null,
            mfe: Number(t.mfe ?? 0),
            mae: Number(t.mae ?? 0),
            cumulative_pnl: Number(t.cumulative_pnl ?? t.cumulativePnl ?? 0),
        };
    });
};
